import { TimePeriodProfit } from '../interfaces';

interface ProfitabilityCellHeader {
	value: string;
	type: 'header';
}

interface ProfitabilityCellDollar {
	value: number;
	type: 'dollar' | 'total';
}

export type ProfitabilityCell =
	| ProfitabilityCellHeader
	| ProfitabilityCellDollar
	| null;
type ProfitabilityRow = ProfitabilityCell[];
type ProfitabilityGroup = ProfitabilityRow[];
export type ProfitabilityGrid = ProfitabilityGroup[];

export type PeriodProfitModelColumn = Pick<
	TimePeriodProfit,
	| 'revenueInCents'
	| 'marginInCents'
	| 'overheadInCents'
	| 'operatingIncomeInCents'
>;

export type PeriodProfitModelType = 'phases' | 'overview';

type ProfitabilityModelConfigBase = {
	type: PeriodProfitModelType;
	columns: PeriodProfitModelColumn[];
};

interface ProfitabilityModelConfigOverview extends ProfitabilityModelConfigBase {
	type: PeriodProfitModelType;
	columns: [PeriodProfitModelColumn, PeriodProfitModelColumn];
}

interface ProfitabilityModelConfigPhases extends ProfitabilityModelConfigBase {
	type: PeriodProfitModelType;
	columns: [
		PeriodProfitModelColumn,
		PeriodProfitModelColumn,
		PeriodProfitModelColumn,
	];
}

export type ProfitabilityModelConfig =
	| ProfitabilityModelConfigOverview
	| ProfitabilityModelConfigPhases;

const HeaderMap: { [key in PeriodProfitModelType]: (string | null)[] } = {
	overview: [
		null,
		'profit.quad.modeling.before',
		'profit.quad.modeling.after',
		'profit.quad.modeling.change',
	],
	phases: [
		null,
		'profit.quad.modeling.before',
		'profit.quad.modeling.phase-1',
		'profit.quad.modeling.phase-2',
	],
};

export class ProfitabilityModel {
	private type!: PeriodProfitModelType;
	private columns!: [
		PeriodProfitModelColumn,
		PeriodProfitModelColumn,
		PeriodProfitModelColumn,
	];

	constructor(config?: ProfitabilityModelConfig) {
		this.type = config?.type ?? 'overview';
		const [first, second] = config?.columns ?? [];
		let [, , third] = config?.columns ?? [];

		if (this.type === 'overview' && first && second) {
			third = this.getChange(first, second);
		}

		this.columns = [
			first ?? DefaultValues(),
			second ?? DefaultValues(),
			third ?? DefaultValues(),
		];
	}

	valueOf(): ProfitabilityGrid {
		const [first, second, third] = this.columns;
		const headers = HeaderMap[this.type];

		return [
			[
				headers.map((h) => (!h ? null : { value: h, type: 'header' })),
				[
					{ value: 'profit.quad.modeling.revenue', type: 'header' },
					{ value: first.revenueInCents, type: 'dollar' },
					{ value: second.revenueInCents, type: 'dollar' },
					{ value: third.revenueInCents, type: 'dollar' },
				],
				[
					{ value: 'profit.quad.modeling.cm$', type: 'header' },
					{ value: first.marginInCents, type: 'dollar' },
					{ value: second.marginInCents, type: 'dollar' },
					{ value: third.marginInCents, type: 'dollar' },
				],
				[
					{ value: 'profit.quad.modeling.oh$', type: 'header' },
					{ value: first.overheadInCents, type: 'dollar' },
					{ value: second.overheadInCents, type: 'dollar' },
					{ value: third.overheadInCents, type: 'dollar' },
				],
			],
			[
				[
					{ value: 'profit.quad.modeling.oi$', type: 'header' },
					{
						value: first.operatingIncomeInCents,
						type: 'total',
					},
					{
						value: second.operatingIncomeInCents,
						type: 'total',
					},
					{
						value: third.operatingIncomeInCents,
						type: 'total',
					},
				],
			],
		];
	}

	readonly getChange = (
		before: PeriodProfitModelColumn | null,
		after: PeriodProfitModelColumn | null,
	): PeriodProfitModelColumn => {
		if (!before || !after) return DefaultValues();

		return {
			revenueInCents: after.revenueInCents - before.revenueInCents,
			marginInCents: after.marginInCents - before.marginInCents,
			overheadInCents: after.overheadInCents - before.overheadInCents,
			operatingIncomeInCents:
				after.operatingIncomeInCents - before.operatingIncomeInCents,
		};
	};
}

const DefaultValues = () => ({
	revenueInCents: 0,
	marginInCents: 0,
	overheadInCents: 0,
	operatingIncomeInCents: 0,
});
